import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import MonthCalendar, { displayYmd } from './MonthCalendar';
import { Calendar as CalendarIcon, Check, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Zoho-style date range filter. Default is "This Month". Selection persists
 * in localStorage per usage key so each page can have its own default.
 *
 * Returns { start, end, label } where start/end are YYYY-MM-DD strings (or null
 * for "All time"). Use them as ?date_start=&date_end= API params.
 */

const FY_START_MONTH = 4; // April — Indian FY

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fyStart(year) {
  // Indian FY runs April 1 → March 31. fyStart(2025) = 2025-04-01.
  return new Date(year, FY_START_MONTH - 1, 1);
}

export const PRESETS = {
  this_month: {
    label: 'This Month',
    compute: () => {
      const t = new Date();
      return {
        start: ymd(new Date(t.getFullYear(), t.getMonth(), 1)),
        end: ymd(new Date(t.getFullYear(), t.getMonth() + 1, 0)),
      };
    },
  },
  last_month: {
    label: 'Last Month',
    compute: () => {
      const t = new Date();
      return {
        start: ymd(new Date(t.getFullYear(), t.getMonth() - 1, 1)),
        end: ymd(new Date(t.getFullYear(), t.getMonth(), 0)),
      };
    },
  },
  last_7_days: {
    label: 'Last 7 Days',
    compute: () => {
      const t = new Date();
      const s = new Date(t);
      s.setDate(t.getDate() - 6);
      return { start: ymd(s), end: ymd(t) };
    },
  },
  last_30_days: {
    label: 'Last 30 Days',
    compute: () => {
      const t = new Date();
      const s = new Date(t);
      s.setDate(t.getDate() - 29);
      return { start: ymd(s), end: ymd(t) };
    },
  },
  this_quarter: {
    label: 'This Quarter',
    compute: () => {
      const t = new Date();
      const q = Math.floor(t.getMonth() / 3);
      return {
        start: ymd(new Date(t.getFullYear(), q * 3, 1)),
        end: ymd(new Date(t.getFullYear(), q * 3 + 3, 0)),
      };
    },
  },
  this_fy: {
    label: 'This FY (Apr–Mar)',
    compute: () => {
      const t = new Date();
      const fy = t.getMonth() + 1 >= FY_START_MONTH ? t.getFullYear() : t.getFullYear() - 1;
      return {
        start: ymd(fyStart(fy)),
        end: ymd(new Date(fy + 1, FY_START_MONTH - 1, 0)),
      };
    },
  },
  last_fy: {
    label: 'Last FY',
    compute: () => {
      const t = new Date();
      const curFy = t.getMonth() + 1 >= FY_START_MONTH ? t.getFullYear() : t.getFullYear() - 1;
      const last = curFy - 1;
      return {
        start: ymd(fyStart(last)),
        end: ymd(new Date(last + 1, FY_START_MONTH - 1, 0)),
      };
    },
  },
  all: {
    label: 'All Time',
    compute: () => ({ start: null, end: null }),
  },
};

export function resolveRange(presetKey, customStart, customEnd) {
  if (presetKey === 'custom') {
    return { start: customStart || null, end: customEnd || null, label: 'Custom' };
  }
  const preset = PRESETS[presetKey] || PRESETS.this_month;
  const r = preset.compute();
  return { start: r.start, end: r.end, label: preset.label };
}

export function useDateRange(storageKey = 'priceintel.dateRange', defaultPreset = 'this_month') {
  const load = () => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch {}
    return { preset: defaultPreset, start: '', end: '' };
  };
  const [state, setStateRaw] = useState(load);

  const setState = (v) => {
    setStateRaw(v);
    try { localStorage.setItem(storageKey, JSON.stringify(v)); } catch {}
  };

  const range = useMemo(() => resolveRange(state.preset, state.start, state.end), [state]);

  return [range, state, setState];
}

/** "6 Oct 2026", or "1 Jun – 22 Oct 2026" (start's year dropped when both share it). */
function rangeLabel(from, to) {
  const a = displayYmd(from);
  if (!a) return null;
  const b = displayYmd(to);
  if (!b || from === to) return a;
  return from.slice(0, 4) === to.slice(0, 4) ? `${a.replace(/ \d{4}$/, '')} – ${b}` : `${a} – ${b}`;
}

// Presets on the left, a travel-app style range calendar on the right (same as
// bookings.deseno.co.in): click a start day, then an end day (the band previews
// on hover); click the same day twice for a single day.
export function DateRangeFilter({ value, onChange, className }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(null); // picked start, waiting for the end
  const [hover, setHover] = useState(null);

  const current = resolveRange(value?.preset, value?.start, value?.end);
  const label = value?.preset === 'custom' ? rangeLabel(current.start, current.end) || 'Custom' : current.label;

  const toggle = (o) => {
    setOpen(o);
    setStart(null);
    setHover(null);
  };

  const pickPreset = (key) => {
    onChange({ preset: key, start: '', end: '' });
    toggle(false);
  };

  const pickDay = (d) => {
    // First click, or a day before the start, (re)starts the range.
    if (!start || d < start) return setStart(d);
    onChange({ preset: 'custom', start, end: d });
    toggle(false);
  };

  // While picking, the band runs start -> hovered day; otherwise it shows the current range.
  const bandStart = start || current.start || undefined;
  const bandEnd = start ? (hover && hover >= start ? hover : undefined) : current.end || undefined;

  return (
    <Popover open={open} onOpenChange={toggle}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn('gap-2 text-sm font-normal', className)} aria-label={`Period: ${label}`}>
          <CalendarIcon className="w-4 h-4" />
          {label}
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={8} className="w-[min(calc(100vw-16px),520px)] p-0 rounded-2xl shadow-xl">
        <div className="flex flex-col sm:flex-row">
          {/* Phones: one scrolling row of presets, so the calendar still fits on screen. */}
          <div className="flex sm:flex-col gap-0.5 p-2 sm:w-44 overflow-x-auto border-b sm:border-b-0 sm:border-r">
            {Object.entries(PRESETS).map(([key, p]) => (
              <button
                key={key}
                type="button"
                onClick={() => pickPreset(key)}
                className={cn(
                  'flex shrink-0 items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg hover:bg-gray-100 text-left whitespace-nowrap',
                  value?.preset === key && 'bg-gray-100 font-medium',
                )}
              >
                <span>{p.label}</span>
                {value?.preset === key && <Check className="w-4 h-4 shrink-0" />}
              </button>
            ))}
          </div>
          <div className="p-3 flex-1 min-w-0">
            {/* Fixed height so the calendar doesn't jump when the hint changes. */}
            <p className="px-1 pb-2 h-6 text-xs text-gray-500 truncate" aria-live="polite">
              {start
                ? <>From <span className="font-medium text-black">{displayYmd(start)}</span>: now pick the end date</>
                : 'Custom range: pick a start date'}
            </p>
            <MonthCalendar selected={bandStart} rangeEnd={bandEnd} onSelect={pickDay} onHover={setHover} />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
